import React, { useEffect, useState, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { apiClient } from "../../../../services/apiClient";
import { ATP_API_URL } from "../../../../config/apiConfig";
import "./index.scss";

/**
 * UploadedDocumentsView
 *
 * Shown only when entity.folderNo exists.
 *
 * 1. GET BULK_UPLOAD_DOCUMENT_VIEW_ALL?folderNo=...
 * 2. For each doc.id → GET VIEW_BULK_UPLOAD_DOCUMENT/{id}  (binary image)
 *
 * Usage:
 *   {entity?.folderNo && (
 *     <UploadedDocumentsView folderNo={entity.folderNo} />
 *   )}
 *   // or
 *   <UploadedDocumentsView entityPrimaryId={entity.folderNo} />
 */
const UploadedDocumentsView = ({ folderNo, entityPrimaryId }) => {
  const effectiveFolderNo = folderNo || entityPrimaryId;

  const [state, setState] = useState("idle"); // idle | loading | loaded | error
  const [documents, setDocuments] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const blobUrlsRef = useRef([]);

  const revokeAllBlobs = () => {
    blobUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];
  };

  useEffect(() => {
    if (!effectiveFolderNo) return;

    let cancelled = false;

    const load = async () => {
      setState("loading");
      setDocuments([]);
      setErrMsg("");
      revokeAllBlobs();

      try {
        // 1. Get document list
        const listRes = await apiClient.get(
          `${ATP_API_URL.BULK_UPLOAD_DOCUMENT_VIEW_ALL}?folderNo=${effectiveFolderNo}`,
          { validateStatus: () => true },
        );

        if (cancelled) return;

        if (listRes?.status !== 200 || !listRes.data?.success) {
          setErrMsg(
            listRes?.data?.message ||
              `Failed to load documents (HTTP ${listRes?.status})`,
          );
          setState("error");
          return;
        }

        const items = listRes.data?.data || [];
        if (items.length === 0) {
          setDocuments([]);
          setState("loaded");
          return;
        }

        // 2. Fetch real image for every id
        const loadedDocs = await Promise.all(
          items.map(async (doc) => {
            try {
              const imgRes = await apiClient.get(
                `${ATP_API_URL.VIEW_BULK_UPLOAD_DOCUMENT}/${doc.id}`,
                {
                  responseType: "blob",
                  validateStatus: () => true,
                },
              );

              if (cancelled) return null;

              if (imgRes?.status === 200 && imgRes.data) {
                const mime =
                  imgRes.data.type ||
                  (doc.fileType === ".png"
                    ? "image/png"
                    : doc.fileType === ".jpg" || doc.fileType === ".jpeg"
                      ? "image/jpeg"
                      : "application/octet-stream");

                const blob = new Blob([imgRes.data], { type: mime });
                const url = URL.createObjectURL(blob);
                blobUrlsRef.current.push(url);

                return { ...doc, imgUrl: url };
              }

              return { ...doc, imgUrl: null, error: true };
            } catch {
              return { ...doc, imgUrl: null, error: true };
            }
          }),
        );

        if (cancelled) return;

        setDocuments(loadedDocs.filter(Boolean));
        setState("loaded");
      } catch (err) {
        if (!cancelled) {
          setErrMsg(err?.message || "Failed to load documents");
          setState("error");
        }
      }
    };

    load();

    return () => {
      cancelled = true;
      revokeAllBlobs();
    };
  }, [effectiveFolderNo]);

  if (!effectiveFolderNo) return null;

  const openFullscreen = (url) => {
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="udv">
      {/* Header */}
      <div className="udv__header">
        <div className="udv__header-left">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
          </svg>
          <span className="udv__title">Uploaded Documents</span>
          <span className="udv__badge">
            {effectiveFolderNo}
            {state === "loaded" && documents.length > 0
              ? ` · ${documents.length}`
              : ""}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="udv__body">
        {state === "loading" && (
          <div className="udv__loading">
            <CircularProgress
              size={30}
              thickness={4}
              sx={{ color: "#2563eb" }}
            />
            <p>Loading documents…</p>
          </div>
        )}

        {state === "error" && (
          <div className="udv__error">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>{errMsg}</p>
          </div>
        )}

        {state === "loaded" && documents.length === 0 && (
          <div className="udv__empty">
            <p>No documents uploaded for this folder.</p>
          </div>
        )}

        {state === "loaded" && documents.length > 0 && (
          <div className="udv__grid">
            {documents.map((doc) => (
              <div className="udv__card" key={doc.id}>
                <div
                  className="udv__card-preview"
                  onClick={() => doc.imgUrl && openFullscreen(doc.imgUrl)}
                >
                  {doc.imgUrl ? (
                    <img
                      className="udv__card-img"
                      src={doc.imgUrl}
                      alt={doc.fileName || "Uploaded document"}
                    />
                  ) : (
                    <div className="udv__card-fallback">
                      {doc.fileType || "FILE"}
                    </div>
                  )}

                  {doc.imgUrl && (
                    <button
                      className="udv__card-fs"
                      onClick={(e) => {
                        e.stopPropagation();
                        openFullscreen(doc.imgUrl);
                      }}
                      title="Open fullscreen"
                    >
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="15 3 21 3 21 9" />
                        <polyline points="9 21 3 21 3 15" />
                        <line x1="21" y1="3" x2="14" y2="10" />
                        <line x1="3" y1="21" x2="10" y2="14" />
                      </svg>
                    </button>
                  )}
                </div>

                <div className="udv__card-footer">
                  <span className="udv__card-name" title={doc.fileName}>
                    {doc.fileName || `Document #${doc.id}`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadedDocumentsView;
